import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from "lucide-react";
import styles from "./MediaPreviewModal.module.scss";

export interface MediaItem {
  type: "image" | "video";
  url: string;
  thumbnailUrl?: string;
  name?: string;
  caption?: string;
}

interface MediaPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: MediaItem[];
  initialIndex?: number;
}

const MediaPreviewModal: React.FC<MediaPreviewModalProps> = ({ isOpen, onClose, items, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const currentItem = items[currentIndex];
  const hasMultiple = items.length > 1;

  // Reset zoom và position khi chuyển item
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [currentIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          if (hasMultiple && currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
          }
          break;
        case "ArrowRight":
          if (hasMultiple && currentIndex < items.length - 1) {
            setCurrentIndex(currentIndex + 1);
          }
          break;
        case "+":
        case "=":
          handleZoomIn();
          break;
        case "-":
          handleZoomOut();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, items.length, hasMultiple, onClose]);

  // Mouse wheel zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (currentItem.type !== "image") return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom((prev) => Math.min(Math.max(prev + delta, 0.5), 3));
    },
    [currentItem],
  );

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.2, 0.5));
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(currentItem.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = currentItem.name || `media-${Date.now()}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  // Drag to pan (chỉ khi zoom > 1)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1 || currentItem.type !== "image") return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.info}>
            {hasMultiple && (
              <span className={styles.counter}>
                {currentIndex + 1} / {items.length}
              </span>
            )}
            {currentItem.name && <span className={styles.name}>{currentItem.name}</span>}
          </div>
          <div className={styles.actions}>
            {currentItem.type === "image" && (
              <>
                <button className={styles.iconButton} onClick={handleZoomOut} title="Zoom out (-)">
                  <ZoomOut size={20} />
                </button>
                <span className={styles.zoomLevel}>{Math.round(zoom * 100)}%</span>
                <button className={styles.iconButton} onClick={handleZoomIn} title="Zoom in (+)">
                  <ZoomIn size={20} />
                </button>
              </>
            )}
            <button className={styles.iconButton} onClick={handleDownload} title="Tải xuống">
              <Download size={20} />
            </button>
            <button className={styles.iconButton} onClick={onClose} title="Đóng (ESC)">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className={styles.content}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: zoom > 1 && currentItem.type === "image" ? (isDragging ? "grabbing" : "grab") : "default" }}
        >
          {currentItem.type === "image" ? (
            <img
              src={currentItem.url}
              alt={currentItem.name || "Preview"}
              className={styles.media}
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                transition: isDragging ? "none" : "transform 0.2s ease",
              }}
              draggable={false}
            />
          ) : (
            <video src={currentItem.url} poster={currentItem.thumbnailUrl} controls className={styles.media} autoPlay />
          )}
        </div>

        {/* Navigation */}
        {hasMultiple && (
          <>
            {currentIndex > 0 && (
              <button className={`${styles.navButton} ${styles.navLeft}`} onClick={handlePrevious} title="Trước (←)">
                <ChevronLeft size={32} />
              </button>
            )}
            {currentIndex < items.length - 1 && (
              <button className={`${styles.navButton} ${styles.navRight}`} onClick={handleNext} title="Sau (→)">
                <ChevronRight size={32} />
              </button>
            )}
          </>
        )}

        {/* Caption */}
        {currentItem.caption && (
          <div className={styles.caption}>
            <p>{currentItem.caption}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaPreviewModal;
